import axios, { AxiosError, type AxiosRequestConfig, type AxiosResponse } from "axios"
import { Agent } from "http"
import { Agent as AgentOverTLS } from "https"
import HTTPClientError from "../error/HTTPClientError.js"
import type { Identity } from "../language/Identity.js"
import HTTPRequest from "./HTTPRequest.js"
import type { HTTPRequestBody } from "./HTTPRequestBody.js"
import HTTPResponse from "./HTTPResponse.js"
import type { HTTPResponseBody } from "./HTTPResponseBody.js"
import AxiosResponseToHTTPResponse from "./mapper/AxiosResponseToHTTPResponse.js"
import HTTPRequestToAxiosRequestConfiguration from "./mapper/HTTPRequestToAxiosRequestConfiguration.js"

/**
 * @description
 *	The main HTTP/HTTPS client.
 *
 * @see https://axios-http.com/docs/intro
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/fetch
 * @see https://nodejs.org/docs/latest-v20.x/api/globals.html#fetch
 */
export default class HTTPClient {
	private readonly httpAgent: Agent
	private readonly httpsAgent: AgentOverTLS
	private readonly proxy: Identity<AxiosRequestConfig<HTTPRequestBody>>

	public constructor(
		configuration: {
			insecureTLS: boolean
			keepAlive: boolean
			proxy: Identity<AxiosRequestConfig<HTTPRequestBody>>
		} = { insecureTLS: false, keepAlive: false, proxy: x => x }
	) {
		this.httpAgent = new Agent({ keepAlive: configuration.keepAlive })
		this.httpsAgent = new AgentOverTLS({
			keepAlive: configuration.keepAlive,
			rejectUnauthorized: !configuration.insecureTLS
		})
		this.proxy = configuration.proxy
	}

	/**
	 * @description
	 *	Sends an HTTP request over the network.
	 */
	public async sendAsync<I, O>(request: HTTPRequest<I>): Promise<HTTPResponse<O>> {
		const axiosRequestConfiguration = this.proxy({
			...HTTPRequestToAxiosRequestConfiguration<I>(request),
			httpAgent: this.httpAgent,
			httpsAgent: this.httpsAgent
		})
		try {
			const axiosResponse = await axios.request<
				HTTPResponseBody,
				AxiosResponse<HTTPResponseBody, HTTPRequestBody>,
				HTTPRequestBody
			>(axiosRequestConfiguration)
			return AxiosResponseToHTTPResponse<O>(axiosResponse)
		} catch (error) {
			if (error instanceof AxiosError) {
				const response =
					error.response === undefined
						? undefined
						: AxiosResponseToHTTPResponse<unknown, unknown>(error.response)
				throw new HTTPClientError(error, response)
			} else {
				throw new Error("Unknown error during request transmission.")
			}
		}
	}
}
